// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC. 

/* eslint-disable spire/avoid-dynamic-translate */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import LazyImage from "@insite/mobius/LazyImage";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rugby/App/Components/Notify";
import { AchParameter } from "@rugby/App/Services/AchService";
import { screen } from "@rugby/XDL/Helpers";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    defualtApprovedforACH?: boolean;
    initialAch: AchParameter;
    getBankNameCallback?: any;
    onSuccessCallback?: any;
    addAchCallback?: any;
}

type Props = OwnProps;

export interface ACHFormStyles {
    wrapper?: InjectableCss;
    routingNumber?: InjectableCss;
    bankName?: InjectableCss;
    accountNicknameWrapper?: InjectableCss;
    accountNumber?: InjectableCss;
    defualtAccountNumber?: InjectableCss;
    checkboxes?: InjectableCss;
    accountType?: InjectableCss;
    addAchButton?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: InjectableCss;
}

export const ACHFormStyles: ACHFormStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    routingNumber: {
        css: css`
            margin-top: 8px;
        `,
    },
    bankName: {
        css: css`
            margin-top: 8px;
        `,
    },
    accountNicknameWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    accountNumber: {
        css: css`
            position: relative;
            margin-top: 8px;
        `,
    },
    defualtAccountNumber: {
        css: css`
            display: flex;
            align-items: center;
            position: absolute;
            top: 32px;
            right: 0px;
            height: 40px;
            padding: 16px;
            background: ${getColor("common.backgroundDisabled")};
            cursor: not-allowed;
        `,
    },
    checkboxes: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 16px;
        `,
    },
    accountType: {
        css: css`
            margin-top: 16px;
        `,
    },
    addAchButton: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            margin-top: 16px;
        `,
    },
    imageWrapper: {
        css: css`
            position: relative;
        `,
    },
    image: {
        css: css`
            position: absolute;
            top: 32px;
            left: calc(100% + 20px);
            width: 100%;
            ${screen("lte", "sm")`
                position: relative;
            `};
        `,
    },
};

const styles = ACHFormStyles;

const accountTypes = [
    { key: 0, value: "Checking" },
    { key: 1, value: "Savings" },
] as any[];

const AchForm: FC<Props> = ({
    addAchCallback,
    defualtApprovedforACH,
    initialAch,
    getBankNameCallback,
    onSuccessCallback,
}) => {
    const [routingNumber, setRoutingNumber] = useState<{ [key: string]: any }>({});
    const [accountNumber, setAccountNumber] = useState<{ [key: string]: any }>({});
    const [accountType, setAccountTypeChange] = useState<{ [key: string]: any }>({
        value: initialAch.accountType || "",
    });
    const [bankName, setBankName] = useState("");
    const [approvedforACH, setApprovedforACH] = useState(true);
    const [image, setImage] = useState("");
    
    const [accountNickname, setAccountNickname] = useState("");

    const onRoutingNumberChange = (value: string) => {
        setRoutingNumber({ ...routingNumber, value });
    };

    const onRoutingNumberBlur = (value: string) => {
        let error = !value ? "Enter valid routing number" : "";
        if (value.length < 9) {
            error = "Routing number must be 9 digit";
        }
        setRoutingNumber({ ...routingNumber, error });
        if (value) {
            getBankNameCallback({
                routingNumber: value,
                onSuccess: (selectedBankName: string) => {
                    setBankName(selectedBankName);
                },
            });
        } else {
            setBankName("");
        }
    };

    const onAccountNumberChange = (value: string) => {
        setAccountNumber({ ...accountNumber, value });
    };

    const onAccountNumberBlur = (value: string) => {
        let error = !value ? "Enter valid account number" : "";
        if (value.length < 5) {
            error = "Please validate your account number";
        }
        setAccountNumber({ ...accountNumber, error });
    };

    const onAccountTypeChange = (value: string) => {
        setAccountTypeChange({ value, error: !value ? "Select Account Type" : "" });
    };

    const handleAccountNicknameChange = (value: string) => {
        setAccountNickname(value);
    };

    const onKeyPressHandler = (event: any) => {
        if (!/^\d+$/.test(event.key)) {
            event.preventDefault();
        }
    };

    const isError = () => {
        if (
            !routingNumber?.value ||
            routingNumber?.value < 9 ||
            !accountNumber?.value ||
            accountNumber?.value.length < 5 ||
            !accountType?.value ||
            routingNumber?.error ||
            accountNumber?.error ||
            accountType?.error
        ) {
            return true;
        }
        return false;
    };

    const submitACH = (event: any) => {
        event.preventDefault();
        addAchCallback({
            accountNumber: `${accountNumber.value}`,
            routingNumber: routingNumber.value,
            bankName,
            accountType: accountType.value,
            approvedForACHProcessing: approvedforACH,
            bankAcctName: accountNickname,
            onSuccess: () => {
                onSuccessCallback(
                    accountNumber.value.substr(accountNumber.value.length - 4, accountNumber.value.length),
                );
            },
        });
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            {defualtApprovedforACH && (
                <Notify
                    style={{ margin: "20px 0" }}
                    messageType="info"
                    body={translate("Please re-enter your bank information")}
                />
            )}
            <StyledWrapper {...styles.routingNumber}>
                <TextField
                    value={routingNumber?.value || ""}
                    error={routingNumber?.error || ""}
                    maxLength={9}
                    onFocus={() => setImage(siteMessage("Routing_No") as string)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onRoutingNumberChange(event.target.value)}
                    onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onRoutingNumberBlur(event.target.value);
                        setImage("");
                    }}
                    onKeyPress={onKeyPressHandler}
                    label={translate("Routing number")}
                    data-test-selector="routing_number"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.bankName}>
                <TextField
                    value={bankName}
                    label={translate("Bank name")}
                    readOnly
                    disabled={true}
                    data-test-selector="bank_name"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.accountNicknameWrapper}>
                <TextField
                    value={accountNickname}
                    onChange={(event) => handleAccountNicknameChange(event.target.value)}
                    label={translate("Account nickname")}
                    data-test-selector="account_nickname"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.imageWrapper}>
                <StyledWrapper>
                    <StyledWrapper {...styles.accountNumber}>
                        <TextField
                            value={accountNumber?.value || ""}
                            error={accountNumber?.error || ""}
                            minLength={5}
                            onFocus={() => setImage(siteMessage("Account_No") as string)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                onAccountNumberChange(event.target.value)
                            }
                            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                                onAccountNumberBlur(event.target.value);
                                setImage("");
                            }}
                            onKeyPress={onKeyPressHandler}
                            label={translate("Account number")}
                            data-test-selector="account_number"
                        />
                    </StyledWrapper>
                </StyledWrapper>
                {image && (
                    <StyledWrapper {...styles.image}>
                        <LazyImage src={image} />
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.accountType}>
                <Select
                    label={translate("Account Type")}
                    value={accountType.value}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onAccountTypeChange(event.target.value)}
                    data-test-selector="accountType"
                >
                    <option value="">{translate("Select Account Type")}</option>
                    {accountTypes.map(at => (
                        <option key={at.key} value={at.value}>
                            {at.value}
                        </option>
                    ))}
                </Select>
            </StyledWrapper>
            <StyledWrapper {...styles.checkboxes}>
                <Checkbox checked={true} disabled={true}>
                    {translate("This order only")}
                </Checkbox>
                <Checkbox
                    checked={approvedforACH}
                    disabled={approvedforACH}
                    onChange={() => setApprovedforACH(!approvedforACH)}
                >
                    {translate("Use for future orders")}
                </Checkbox>
            </StyledWrapper>
            <StyledWrapper {...styles.addAchButton}>
                <Button data-test-selector="add_ACH" disabled={isError()} onClick={submitACH}>
                    {!defualtApprovedforACH ? translate("Add Bank") : translate("Update Bank")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

AchForm.defaultProps = {
    defualtApprovedforACH: false,
    initialAch: undefined,
};

export default AchForm;
